// ============================================================
// SPELL RESOLUTION — effects-list model (Session 10 refactor)
// ============================================================
//
// Each spell is a SpellDef with:
//   - phase   : 'friendly' (Step A, tick START) / 'hostile' (Step D, after combat)
//   - scope   : 'own' / 'enemy' default target filter for effects
//   - radius  : Manhattan radius around the cast target
//   - effects : ReadonlyArray<EffectKind> — applied in order
//
// `resolveSpells` walks the cast list and, for each cast whose phase
// matches the requested sub-step, iterates `sp.effects` and dispatches
// each EffectKind to its handler in `_applyEffect` / `_applyWhirlwind`.
//
// Per-effect output goes into the SpellCastRecord:
//   - numeric mutations (damage / heal / buff / shield) → SpellImpact[]
//     (one impact per affected unit per effect; field-tagged for the
//     client's per-impact dispatch — see Game._renderSpellFx)
//   - whirlwindPull displacements → MoveRecord[]
//
// Bases are never affected by spells.
//
// Backwards compatibility:
//   - HEAL_RATIO / LIGHTNING_PUDDLE_BONUS / defenseReduction are still
//     re-exported below so old test files / external callers don't break.
//   - SpellImpact wire shape unchanged; new model just emits more impacts
//     per cast (one per effect-unit pair) instead of one fat impact.

import { md } from '../utils.ts';
import {
  BATTLE_COLS, BATTLE_ROWS, ELEM, SPELLS, UCFG,
  type EffectKind, type EffectScaling, type SpellDef, type UnitModifier,
} from '../constants.ts';
import {
  HEAL_RATIO,
  LIGHTNING_PUDDLE_BONUS,
  GROW_ROOT_DURATION_TICKS,
  GROW_DEFDOWN_DURATION_TICKS,
  defenseReduction,
} from '../balance.ts';
import type { BattleUnit, Base, Pos } from '../BattleUnit.ts';
import type {
  MoveRecord,
  SpellCastRecord,
  SpellCommand,
  SpellImpact,
  SpellPhase,
} from './types.ts';
import { applyDamageToUnit } from './damage.ts';
import {
  getTerrainAt,
  growTransition,
  rainTransition,
  type TerrainCell,
  type TerrainGrid,
} from './terrain.ts';
import type { TerrainGrowEvent, TerrainRainEvent } from './types.ts';

// Re-export for back-compat (tests + external callers import these from spell.ts).
// 想改數值請編輯 src/balance.ts，不是這裡。
export { HEAL_RATIO, LIGHTNING_PUDDLE_BONUS, defenseReduction };

// ---------------------------------------------------------------
// EffectScaling resolver
// ---------------------------------------------------------------

// ---------------------------------------------------------------
// Unit modifier helpers (Session 10 follow-up — durable per-spell mods)
// ---------------------------------------------------------------

/**
 * Recompute the cached `buffAtk` / `tickDefPhys` / `tickDefMag` scalars
 * from the unit's current `modifiers` list. Combat.ts + spell.ts hostile
 * still read these scalar fields; we keep them in sync after every
 * push / expire so consumer code doesn't need to know about modifiers.
 *
 * Stacking rules (mirror prior tick-scoped semantics):
 *   - buffAtk     : SUM across modifiers (Bless + Whirlwind hostile
 *                   debuff stack additively when they coexist)
 *   - shieldPhys  : MAX (high-power shield "absorbs" any weaker one)
 *   - shieldMag   : MAX (same)
 */
export function _recomputeScalars(u: BattleUnit): void {
  let buff = 0, phys = 0, mag = 0, rooted = false, defDownSum = 0;
  for (const m of u.modifiers) {
    if (m.kind === 'buffAtk') buff += m.value;
    else if (m.kind === 'shieldPhys') { if (m.value > phys) phys = m.value; }
    else if (m.kind === 'shieldMag')  { if (m.value > mag)  mag  = m.value; }
    else if (m.kind === 'rooted')     { rooted = true; }
    // Session 11 follow-up — defDown is SUM-aggregated (multiple debuffs
    // stack additively). Cached as `dmgTakenMult = 1 + sum` so the read
    // site in `applyDamageToUnit` is a single multiply.
    else if (m.kind === 'defDown')    { defDownSum += m.value; }
  }
  u.buffAtk = buff;
  u.tickDefPhys = phys;
  u.tickDefMag = mag;
  // Session 11 — rooted is a boolean OR-aggregate; one source is enough.
  u.rooted = rooted;
  // Session 11 follow-up — dmgTakenMult is a vulnerability multiplier:
  // 1.0 = baseline, >1 = more damage taken. Clamp at 1 to prevent any
  // (mis-signed) defDown from becoming a damage reduction.
  u.dmgTakenMult = 1 + Math.max(0, defDownSum);
}

/**
 * Apply (or refresh) a modifier on `u`. Same-source modifiers DO NOT
 * STACK — a (kind, source) match replaces the existing entry's
 * `value` + `ticksRemaining` so a second cast of the same spell refreshes
 * the debuff rather than compounding it. Cross-source still coexists.
 *
 * Caller usually follows with `_recomputeScalars(u)` (which this fn
 * does itself) so scalar reads see the new state immediately.
 *
 * `durationTicks` semantics: modifier survives N end-of-tick decrements.
 * `tick.ts` end-of-tick runs once per tick; expiry is checked there.
 * Effective combat coverage depends on cast phase — see UnitModifier
 * doc block in constants.ts.
 */
export function _pushModifier(
  u: BattleUnit,
  kind: UnitModifier['kind'],
  value: number,
  durationTicks: number,
  source: number,
): void {
  const existing = u.modifiers.findIndex((m) => m.kind === kind && m.source === source);
  if (existing >= 0) {
    // Refresh: replace value, ticksRemaining, AND startingTicks. The
    // client-side badge fade ratio (ticksRemaining/startingTicks) needs
    // a fresh "100%" baseline when a debuff is re-applied.
    u.modifiers[existing].value = value;
    u.modifiers[existing].ticksRemaining = durationTicks;
    u.modifiers[existing].startingTicks = durationTicks;
  } else {
    u.modifiers.push({
      kind,
      value,
      ticksRemaining: durationTicks,
      startingTicks: durationTicks,
      source,
    });
  }
  _recomputeScalars(u);
}

/**
 * Resolve an `EffectScaling` to a concrete number at a given `power`.
 *   - flat   → constant
 *   - linear → perLevel × power
 *   - table  → byPower[power] (clamped to last entry on overflow,
 *              0 on negative power as a safety net)
 *
 * Used by every numeric effect (damage / heal / buffAtk / shield*).
 */
function _scaleValue(scaling: EffectScaling, power: number): number {
  switch (scaling.kind) {
    case 'flat': return scaling.value;
    case 'linear': return scaling.perLevel * power;
    case 'table': {
      const arr = scaling.byPower;
      if (arr.length === 0) return 0;
      const idx = Math.max(0, Math.min(power, arr.length - 1));
      return arr[idx];
    }
  }
}

// ---------------------------------------------------------------
// Public API
// ---------------------------------------------------------------

/**
 * Optional inputs for terrain-aware spell resolution.
 *
 * - `terrain`        : authoritative grid; mutated in place when a
 *                      damage effect with `ignite: true` lands on
 *                      grass. Pass `undefined` to disable terrain
 *                      interaction (legacy / no-terrain fixtures).
 * - `ignitedCells`   : OUT param. Each `'grass'` → `'burningGrass'`
 *                      transition pushes its WORLD-coord pos here. Caller
 *                      typically passes a fresh `Pos[]` buffer and emits
 *                      a `terrainIgnite` event with whatever lands inside.
 * - `bases`          : needed by `whirlwindPull` to reject destinations
 *                      that would land a unit on a base tile (design Q3).
 *                      Optional for back-compat with old fixtures.
 */
export interface SpellResolveOptions {
  terrain?: TerrainGrid;
  ignitedCells?: Pos[];
  /**
   * Session 11 (Leader CCC) — OUT param. Each terrain transition caused
   * by a `growGrass` effect pushes `{x, y, to}` here (world coords).
   * The `to` field is the destination terrain (e.g. `'grass'` for an
   * empty-cell paint, `'swamp'` when grass is grown on a puddle, etc.)
   * — see `growTransition` in terrain.ts. Caller emits a `terrainGrow`
   * event from whatever lands inside (suppressed if empty). Optional;
   * pass undefined to skip the tracking (terrain still mutates).
   */
  grownCells?: TerrainGrowEvent['cells'];
  /**
   * Session 11 (Leader CCC) — OUT param. Each terrain transition caused
   * by a `rainPuddle` effect pushes `{x, y, to}` here. See
   * `rainTransition` in terrain.ts for the table.
   */
  rainedCells?: TerrainRainEvent['cells'];
  /**
   * Session 12 (Leader CCC, Rockfall) — OUT param. Each boulder dropped
   * on an empty target cell pushes `{x, y}` (world coords) here. Caller
   * emits a `terrainBoulderFall` event from whatever lands inside.
   */
  boulderFallCells?: Pos[];
  /**
   * Session 12 (Leader CCC counter) — OUT param. Each `'boulder'` cell
   * shattered by a Fire / Lightning damage effect with
   * `breakBoulder: true` pushes `{x, y}` (world coords) here. Caller
   * emits a `terrainBoulderBreak` event.
   */
  boulderBreakCells?: Pos[];
  bases?: Base[];
}

/**
 * Resolve every cast belonging to `phase` (Step A or Step D) in the
 * order given. Mutates `units` (and optionally `opts.terrain`) in place.
 *
 * Phase mismatch and `spellType === 0` (Pass) entries are skipped.
 */
export function resolveSpells(
  units: BattleUnit[],
  casts: { team: number; cmd: SpellCommand }[],
  phase: SpellPhase,
  opts?: SpellResolveOptions,
): SpellCastRecord[] {
  const records: SpellCastRecord[] = [];
  for (const { team, cmd } of casts) {
    if (!cmd || cmd.spellType === 0) continue;
    const sp = SPELLS[ELEM[cmd.spellType]];
    if (!sp) continue;
    if (sp.phase !== phase) continue;

    // All non-Pass spells require a target tile (UI enforces). Fall back
    // to (4,4) only as a safety net for legacy fixtures.
    const tgt: Pos = cmd.targetPos || { x: 4, y: 4 };
    const power = cmd.powerLevel || 1;

    const impacts: SpellImpact[] = [];
    let moves: MoveRecord[] | undefined;

    // Session 12 follow-up — snapshot occupancy at cast START (any alive
    // unit of any team) so the rockfall boulder paint can see "was this
    // cell occupied AT cast time?". Without this, a rockfall that kills
    // its target would mark the cell as empty (dead unit filtered out)
    // and erroneously drop a boulder on the corpse.
    //
    // Only computed when the spell has a `rockfall` effect — keeps the
    // hot path for damage / heal / etc unchanged.
    let occupiedAtCastStart: Set<string> | undefined;
    for (const eff of sp.effects) {
      if (eff.kind === 'rockfall') {
        occupiedAtCastStart = new Set<string>();
        for (const u of units) {
          if (u.dead) continue;
          occupiedAtCastStart.add(`${u.pos.x},${u.pos.y}`);
        }
        break;
      }
    }

    for (const eff of sp.effects) {
      if (eff.kind === 'whirlwindPull') {
        const out = _applyWhirlwind(units, team, sp, eff, tgt, opts?.bases);
        if (out.length > 0) {
          moves = moves ? moves.concat(out) : out;
        }
      } else {
        // Session 10 — pass cmd.spellType as `sourceElem` so modifiers
        // pushed by buffAtk/shield effects can match-and-replace future
        // casts of the same spell (same-source non-stacking rule).
        _applyEffect(eff, units, team, sp, tgt, power, cmd.spellType, opts, impacts);
      }
    }

    // Fire-style ignite: any damage effect flagged `ignite` ignites grass
    // in the spell's radius. Multiple ignite effects in one spell are
    // idempotent because `_igniteGrassInRadius` skips non-grass cells.
    if (opts?.terrain) {
      for (const eff of sp.effects) {
        if (eff.kind === 'damage' && eff.ignite) {
          _igniteGrassInRadius(opts.terrain, tgt, sp.radius, opts.bases, opts.ignitedCells);
        }
      }
    }

    // Session 12 — Fire / Lightning shatter `'boulder'` cells in their
    // radius (the damage effect carries `breakBoulder: true`). Runs
    // alongside ignite — both pass through every cell in radius and
    // mutate the terrain in place. Independent of whether a legitimate
    // damage target stood there (boulders cover empty tiles, so most of
    // the time there's nothing to damage AT the boulder cell anyway).
    if (opts?.terrain) {
      for (const eff of sp.effects) {
        if (eff.kind === 'damage' && eff.breakBoulder) {
          _breakBouldersInRadius(opts.terrain, tgt, sp.radius, opts.boulderBreakCells);
        }
      }
    }

    // Session 11 (Leader CCC) — Grow / Rain terrain painting. Runs AFTER
    // unit-side effects (above) because _applyEffect's `growGrass` /
    // `rainPuddle` handlers read the PRE-cast terrain to decide whether
    // to root / damage a standing unit. Mutating cells first would let a
    // freshly-painted grass cell root a unit that wasn't on grass before,
    // which is the opposite of the design intent ("既有地形" 才觸發).
    //
    // Session 11 (swamp / base-fix) — uses growTransition / rainTransition
    // tables (not a single hardcoded paint kind) so the asymmetric
    // conversions land correctly:
    //   Grow:  none→grass / puddle→swamp / swamp→grass / grass→Ø / burningGrass→Ø
    //   Rain:  none→puddle / grass→swamp / swamp→puddle / puddle→Ø / burningGrass→Ø
    // Also skips base tiles — fixes the "草地覆蓋到基地" bug where Grow
    // on a base tile would paint grass on top of bases.
    if (opts?.terrain) {
      for (const eff of sp.effects) {
        if (eff.kind === 'growGrass') {
          _applyTerrainTransition(opts.terrain, tgt, sp.radius, growTransition, opts.bases, opts.grownCells);
        } else if (eff.kind === 'rainPuddle') {
          _applyTerrainTransition(opts.terrain, tgt, sp.radius, rainTransition, opts.bases, opts.rainedCells);
        } else if (eff.kind === 'rockfall') {
          // Session 12 — Rockfall paint pass. Only cells with NO unit
          // AT CAST START (snapshot above) flip to 'boulder'; cells
          // where the rock shattered against a standing unit are left
          // as-is, including the case where the unit died this cast
          // (otherwise the rock would magically drop on the corpse).
          // Base tiles are always skipped. Boulder is unconditional —
          // overwrites any existing terrain (none / grass / puddle /
          // swamp / burningGrass).
          _applyBoulderFall(
            opts.terrain, tgt, sp.radius,
            occupiedAtCastStart ?? new Set<string>(),
            opts.bases, opts.boulderFallCells,
          );
        }
      }
    }

    records.push({
      team,
      cmd,
      sp,
      tgt,
      impacts,
      ...(moves && moves.length > 0 ? { moves } : {}),
    });
  }
  return records;
}

// ---------------------------------------------------------------
// Per-effect handlers
// ---------------------------------------------------------------

/**
 * True if `u` matches the effect's scope (defaulting to spell-level scope)
 * relative to the casting `team`.
 *
 * Session 12 follow-up — `'all'` scope makes the effect hit BOTH teams
 * (friendly fire). Used by damage spells per design: Fire / Lightning /
 * Rockfall / Rain damage every unit in range regardless of team.
 */
function _matchesScope(u: BattleUnit, team: number, eff: EffectKind, sp: SpellDef): boolean {
  // whirlwindPull doesn't carry a scope field on the union variant
  // because it's handled by `_applyWhirlwind`; treat as spell scope.
  const effScope = (eff as { scope?: 'own' | 'enemy' | 'all' }).scope;
  const scope = effScope ?? sp.scope;
  if (scope === 'all') return true;
  const isOwn = u.team === team;
  if (scope === 'own') return isOwn;
  return !isOwn;
}

/**
 * Apply one EffectKind (non-whirlwind) to every unit within range that
 * matches the scope filter. Each affected unit gets ONE SpellImpact
 * pushed into `impacts`, tagged with the relevant field (dmg / heal /
 * buff / defPhys / defMag) so the client can dispatch visuals per impact.
 */
function _applyEffect(
  eff: EffectKind,
  units: BattleUnit[],
  team: number,
  sp: SpellDef,
  tgt: Pos,
  power: number,
  sourceElem: number,
  opts: SpellResolveOptions | undefined,
  impacts: SpellImpact[],
): void {
  for (const u of units) {
    if (u.dead) continue;
    if (!_matchesScope(u, team, eff, sp)) continue;
    if (md(u.pos, tgt) > sp.radius) continue;

    switch (eff.kind) {
      case 'damage': {
        let rawDmg = _scaleValue(eff.amount, power);
        // Lightning-style puddle bonus: applied BEFORE tickDefMag.
        // Per-target judged so a radius-1 spell can mix in/out puddle hits.
        if (
          eff.puddleBonus &&
          opts?.terrain &&
          getTerrainAt(opts.terrain, u.pos.x, u.pos.y) === 'puddle'
        ) {
          rawDmg *= eff.puddleBonus;
        }
        const reduce = u.tickDefMag || 0;
        const finalDmg = reduce > 0 ? Math.round(rawDmg * (1 - reduce)) : rawDmg;
        const delta = applyDamageToUnit(u, finalDmg);
        impacts.push({
          unitId: u.id,
          dmg: delta.dmg,
          died: delta.died,
          totalSoldiers: delta.totalSoldiers,
        });
        break;
      }
      case 'heal': {
        const cfg = UCFG[u.type];
        const maxSoldiers = cfg ? cfg.hp : u.totalSoldiers;
        const ratio = _scaleValue(eff.ratio, power);
        if (ratio <= 0) continue; // skip impact-emit for "no heal this level"
        const heal = Math.ceil(maxSoldiers * ratio);
        if (heal <= 0) continue;
        const before = u.totalSoldiers;
        u.totalSoldiers = Math.min(maxSoldiers, before + heal);
        impacts.push({
          unitId: u.id,
          heal: u.totalSoldiers - before,
          totalSoldiers: u.totalSoldiers,
        });
        break;
      }
      case 'buffAtk': {
        const value = _scaleValue(eff.value, power);
        if (value === 0) continue; // no-op impact: skip
        // Session 10 — push to modifier list; _pushModifier recomputes
        // u.buffAtk so combat in the SAME tick (when phase='friendly')
        // reads the updated value. End-of-tick decrements + expires.
        _pushModifier(u, 'buffAtk', value, eff.durationTicks ?? 1, sourceElem);
        impacts.push({
          unitId: u.id,
          buff: value,
          totalSoldiers: u.totalSoldiers,
        });
        break;
      }
      case 'shieldPhys': {
        const reduction = _scaleValue(eff.reduction, power);
        if (reduction <= 0) continue;
        // Session 10 — same as buffAtk: push to modifier list; scalar
        // gets recomputed (MAX stacking rule). Same-source replace lets
        // a stronger re-cast bump up the value, weaker re-cast drops it.
        _pushModifier(u, 'shieldPhys', reduction, eff.durationTicks ?? 1, sourceElem);
        impacts.push({
          unitId: u.id,
          defPhys: reduction,
          totalSoldiers: u.totalSoldiers,
        });
        break;
      }
      case 'shieldMag': {
        const reduction = _scaleValue(eff.reduction, power);
        if (reduction <= 0) continue;
        _pushModifier(u, 'shieldMag', reduction, eff.durationTicks ?? 1, sourceElem);
        impacts.push({
          unitId: u.id,
          defMag: reduction,
          totalSoldiers: u.totalSoldiers,
        });
        break;
      }
      case 'growGrass': {
        // Session 11 (Leader CCC) — Grow's per-unit branch:
        //   - target on pre-existing 'grass' → root (durationTicks=2 hostile)
        //   - target on anything else        → defDown debuff (Session 11
        //                                       follow-up; makes Grow useful
        //                                       even on open ground)
        // The terrain-painting half (asymmetric — see growTransition in
        // terrain.ts) runs AFTER this pass via `_applyTerrainTransition`
        // in `resolveSpells`, so freshly-painted cells don't retro-root
        // or retro-debuff this turn's units.
        //
        // burningGrass is intentionally NOT eligible for root (Fire+Grow
        // combos don't double-dip; the unit takes the defDown branch
        // instead, which still levels the playing field somewhat).
        if (!opts?.terrain) continue;
        const cell = getTerrainAt(opts.terrain, u.pos.x, u.pos.y);
        if (cell === 'grass') {
          // Branch A — root.
          const dur = eff.rootDurationTicks ?? GROW_ROOT_DURATION_TICKS;
          _pushModifier(u, 'rooted', 1, dur, sourceElem);
          impacts.push({
            unitId: u.id,
            rooted: dur,
            totalSoldiers: u.totalSoldiers,
          });
        } else if (eff.defDownAmount) {
          // Branch B — defense down (vulnerability +X%). Skipped silently
          // if the SpellDef omits `defDownAmount` (back-compat with older
          // fixtures that only configured the root half).
          const defDownAmt = _scaleValue(eff.defDownAmount, power);
          if (defDownAmt <= 0) break;
          const dur = eff.defDownDurationTicks ?? GROW_DEFDOWN_DURATION_TICKS;
          _pushModifier(u, 'defDown', defDownAmt, dur, sourceElem);
          impacts.push({
            unitId: u.id,
            defDown: defDownAmt,
            totalSoldiers: u.totalSoldiers,
          });
        }
        break;
      }
      case 'rockfall': {
        // Session 12 (Leader CCC) — Rockfall's per-unit branch:
        //   - For each enemy unit on a cell within radius (= just the
        //     center cell for radius=0): the rock shatters and deals
        //     `ceil(maxSoldiers × dmgRatio[power])` magical damage.
        // The boulder-paint half runs AFTER this pass (see resolveSpells)
        // and checks `units.some(...)` to decide whether to drop a
        // boulder. We use friend-or-foe occupancy there so a friendly
        // unit on the cell ALSO prevents the rock from settling — even
        // though no damage is dealt to friendlies (scope filter excludes
        // them above), the cast still whiffs cleanly with no terrain
        // change. This matches the user-facing description: "if there's
        // a unit on the cell, the rock shatters; otherwise it stays".
        const cfg = UCFG[u.type];
        const maxSoldiers = cfg ? cfg.hp : u.totalSoldiers;
        const ratio = _scaleValue(eff.dmgRatio, power);
        if (ratio <= 0) continue;
        const rawDmg = Math.ceil(maxSoldiers * ratio);
        if (rawDmg <= 0) continue;
        const reduce = u.tickDefMag || 0;
        const finalDmg = reduce > 0 ? Math.round(rawDmg * (1 - reduce)) : rawDmg;
        const delta = applyDamageToUnit(u, finalDmg);
        impacts.push({
          unitId: u.id,
          dmg: delta.dmg,
          died: delta.died,
          totalSoldiers: delta.totalSoldiers,
        });
        break;
      }
      case 'rainPuddle': {
        // Session 11 (Leader CCC) — Rain's per-unit branch:
        //   - target on pre-existing 'puddle' → big % maxSoldiers damage
        //   - target on anything else         → small "drizzle" damage
        //                                        (Session 11 follow-up; the
        //                                        directDmgRatio table is
        //                                        capped low by design)
        // Both branches go through the magic damage path (tickDefMag
        // reduction + applyDamageToUnit, which folds in dmgTakenMult).
        if (!opts?.terrain) continue;
        const cell = getTerrainAt(opts.terrain, u.pos.x, u.pos.y);
        const cfg = UCFG[u.type];
        const maxSoldiers = cfg ? cfg.hp : u.totalSoldiers;
        let ratio = 0;
        if (cell === 'puddle') {
          ratio = _scaleValue(eff.drownRatio, power);
        } else if (eff.directDmgRatio) {
          ratio = _scaleValue(eff.directDmgRatio, power);
        }
        if (ratio <= 0) continue;
        const rawDmg = Math.ceil(maxSoldiers * ratio);
        if (rawDmg <= 0) continue;
        const reduce = u.tickDefMag || 0;
        const finalDmg = reduce > 0 ? Math.round(rawDmg * (1 - reduce)) : rawDmg;
        const delta = applyDamageToUnit(u, finalDmg);
        impacts.push({
          unitId: u.id,
          dmg: delta.dmg,
          died: delta.died,
          totalSoldiers: delta.totalSoldiers,
        });
        break;
      }
      // whirlwindPull handled by _applyWhirlwind; never reaches here.
    }
  }
}

// ---------------------------------------------------------------
// Whirlwind displacement (Session 9, unchanged in Session 10 refactor)
// ---------------------------------------------------------------

/**
 * Mutate `terrain` so every `'grass'` cell within Manhattan `radius` of
 * `center` becomes `'burningGrass'`. Out-of-range / non-grass cells are
 * untouched. Each transition pushes `{x,y}` (world coords) into `out` if
 * provided. Caller emits a `terrainIgnite` event from `out`.
 */
function _igniteGrassInRadius(
  terrain: TerrainGrid,
  center: Pos,
  radius: number,
  bases: Base[] | undefined,
  out: Pos[] | undefined,
): void {
  // Session 11 (base-fix) — skip base tiles defensively. Currently the
  // only way grass can land on a base is via Grow (also now base-safe),
  // so this is belt-and-suspenders; but if a future spell ever paints
  // grass on bases, ignite stays safe without a separate fix here.
  const baseTiles = new Set<string>();
  if (bases) {
    for (const b of bases) {
      for (const t of b.tiles) baseTiles.add(`${t.x},${t.y}`);
    }
  }
  for (let dy = -radius; dy <= radius; dy++) {
    const remX = radius - Math.abs(dy);
    for (let dx = -remX; dx <= remX; dx++) {
      const x = center.x + dx;
      const y = center.y + dy;
      if (baseTiles.has(`${x},${y}`)) continue;
      if (getTerrainAt(terrain, x, y) === 'grass') {
        terrain[y][x] = 'burningGrass';
        if (out) out.push({ x, y });
      }
    }
  }
}

/**
 * Session 11 (Leader CCC; swamp + base-fix follow-up) — apply a terrain
 * transition function over every cell within Manhattan `radius` of
 * `center`. The transition `fn` returns the NEW cell for each (current)
 * cell, or `null` to leave the cell unchanged. Use `growTransition` for
 * Grow casts and `rainTransition` for Rain casts (defined in terrain.ts).
 *
 * Base tiles are ALWAYS skipped — fixes the "草地覆蓋到基地" bug where
 * Grow casts near the enemy base would paint grass on the base itself.
 * Bases are treated as terrain-immune (matches: bases are never affected
 * by spells per the existing `_applyEffect` design).
 *
 * Each non-null transition mutates `terrain[y][x]` and (if `out` is
 * provided) pushes `{ x, y, to }` — the `to` field is the destination
 * terrain so the client can apply the change idempotently regardless of
 * its predicted state (transitions are asymmetric — see TerrainGrowEvent
 * doc in types.ts).
 */
/**
 * Session 12 (Leader CCC, Rockfall) — drop a `'boulder'` block on every
 * cell within Manhattan `radius` of `center` that had NO unit on it at
 * cast start. `occupiedAtCastStart` is the caller-supplied snapshot of
 * `{x,y}` keys for every alive unit (friend or foe) AT CAST TIME — units
 * killed by this same cast are still considered "occupying" their cell,
 * so the rock that shattered against them doesn't magically drop a
 * boulder on the corpse afterward (Session 12 follow-up bug fix).
 *
 * Base tiles are also skipped defensively (bases never take terrain).
 * Boulder paint is unconditional w.r.t. existing terrain: any of
 * `'none' / 'grass' / 'puddle' / 'swamp' / 'burningGrass'` becomes
 * `'boulder'` (the boulder literally covers the previous terrain —
 * design intent).
 *
 * radius=0 in the current SPELLS.Rockfall config narrows this to the
 * single center cell, but the function is written generically so a
 * future Rockfall variant could AOE drop boulders.
 */
function _applyBoulderFall(
  terrain: TerrainGrid,
  center: Pos,
  radius: number,
  occupiedAtCastStart: Set<string>,
  bases: Base[] | undefined,
  out: Pos[] | undefined,
): void {
  const baseTiles = new Set<string>();
  if (bases) {
    for (const b of bases) {
      for (const t of b.tiles) baseTiles.add(`${t.x},${t.y}`);
    }
  }
  for (let dy = -radius; dy <= radius; dy++) {
    const remX = radius - Math.abs(dy);
    for (let dx = -remX; dx <= remX; dx++) {
      const x = center.x + dx;
      const y = center.y + dy;
      if (y < 0 || y >= BATTLE_ROWS) continue;
      if (x < 0 || x >= BATTLE_COLS) continue;
      if (baseTiles.has(`${x},${y}`)) continue;
      // Cell had a unit at cast start (whether or not it survived this
      // cast) → the rock shattered, no boulder spawns.
      if (occupiedAtCastStart.has(`${x},${y}`)) continue;
      const cur = getTerrainAt(terrain, x, y);
      if (cur === 'boulder') continue; // already a boulder — no-op write
      terrain[y][x] = 'boulder';
      if (out) out.push({ x, y });
    }
  }
}

/**
 * Session 12 (Leader CCC counter) — Fire / Lightning shatter `'boulder'`
 * cells in their spell radius back to `'none'`. Only cells currently
 * holding a boulder are affected (other terrain types are silently
 * ignored). World-coord transitions get pushed into `out` so the caller
 * can emit a `terrainBoulderBreak` event.
 *
 * Skips base tiles defensively even though bases never hold boulders —
 * matches the symmetry of `_igniteGrassInRadius` / `_applyTerrainTransition`.
 */
function _breakBouldersInRadius(
  terrain: TerrainGrid,
  center: Pos,
  radius: number,
  out: Pos[] | undefined,
): void {
  for (let dy = -radius; dy <= radius; dy++) {
    const remX = radius - Math.abs(dy);
    for (let dx = -remX; dx <= remX; dx++) {
      const x = center.x + dx;
      const y = center.y + dy;
      if (y < 0 || y >= BATTLE_ROWS) continue;
      if (x < 0 || x >= BATTLE_COLS) continue;
      if (getTerrainAt(terrain, x, y) !== 'boulder') continue;
      terrain[y][x] = 'none';
      if (out) out.push({ x, y });
    }
  }
}

function _applyTerrainTransition(
  terrain: TerrainGrid,
  center: Pos,
  radius: number,
  fn: (current: TerrainCell) => TerrainCell | null,
  bases: Base[] | undefined,
  out: { x: number; y: number; to: TerrainCell }[] | undefined,
): void {
  // Build base-tile lookup once per cast (cheap — ≤ 4 tiles).
  const baseTiles = new Set<string>();
  if (bases) {
    for (const b of bases) {
      for (const t of b.tiles) baseTiles.add(`${t.x},${t.y}`);
    }
  }
  for (let dy = -radius; dy <= radius; dy++) {
    const remX = radius - Math.abs(dy);
    for (let dx = -remX; dx <= remX; dx++) {
      const x = center.x + dx;
      const y = center.y + dy;
      if (y < 0 || y >= BATTLE_ROWS) continue;
      if (x < 0 || x >= BATTLE_COLS) continue;
      // Skip base tiles — bases don't take terrain.
      if (baseTiles.has(`${x},${y}`)) continue;
      const cur = getTerrainAt(terrain, x, y);
      const next = fn(cur);
      if (next === null) continue;
      // Defensive: don't emit a no-op transition (cur === next).
      if (next === cur) continue;
      terrain[y][x] = next;
      if (out) out.push({ x, y, to: next });
    }
  }
}

/**
 * Whirlwind effect — pulls every in-range unit matching the spell's scope
 * one cell toward `center` on the 8-direction axis (diagonals allowed).
 *
 * Blocking rules (unit stays put on its turn if):
 *   - destination out of battlefield bounds
 *   - destination is a base tile (bases unaffected by spells)
 *   - destination has, in the PRE-CAST snapshot, a non-candidate unit on
 *     the opposing team (hostile static blocker). Same-team static units
 *     are fine — friendly stacking is allowed; sibling candidates fine
 *     because they're also moving (snapshot pattern lets two candidates
 *     swap positions).
 *
 * Path preservation (Session 10 follow-up — fix for "whirlwind ate my path"):
 *   The unit's `u.path` / `u.pathIdx` are KEPT after displacement so the
 *   player's planned route still applies. `u.pathIdx` is advanced to the
 *   CLOSEST remaining waypoint (Manhattan distance from `dest`) so the
 *   unit "fast-forwards" past waypoints it was blown across, and falls
 *   back to walking toward an earlier waypoint if it was blown sideways
 *   or backward. movement.ts's priority-2 step uses `_stepToward` to
 *   single-axis walk toward the next waypoint (might take 1-2 extra
 *   ticks to re-align), instead of teleporting straight onto it.
 */
function _applyWhirlwind(
  units: BattleUnit[],
  team: number,
  sp: SpellDef,
  eff: EffectKind,
  center: Pos,
  bases: Base[] | undefined,
): MoveRecord[] {
  const candidates: BattleUnit[] = [];
  for (const u of units) {
    if (u.dead) continue;
    if (!_matchesScope(u, team, eff, sp)) continue;
    if (md(u.pos, center) > sp.radius) continue;
    const dx = Math.sign(center.x - u.pos.x);
    const dy = Math.sign(center.y - u.pos.y);
    if (dx === 0 && dy === 0) continue; // already at center
    candidates.push(u);
  }
  if (candidates.length === 0) return [];

  // Initial snapshot of unit positions — built from ALL units, since a
  // static (non-candidate) unit at a candidate's destination can still
  // block the move if it's an enemy of that candidate.
  type Snap = { unit: BattleUnit; pos: Pos };
  const snapByCell = new Map<string, Snap>();
  for (const u of units) {
    if (u.dead) continue;
    snapByCell.set(`${u.pos.x},${u.pos.y}`, { unit: u, pos: { x: u.pos.x, y: u.pos.y } });
  }
  const candSet = new Set<BattleUnit>(candidates);

  // Pre-compute base-tile lookup once per cast.
  const baseTiles = new Set<string>();
  if (bases) {
    for (const b of bases) {
      for (const t of b.tiles) baseTiles.add(`${t.x},${t.y}`);
    }
  }

  const moves: MoveRecord[] = [];
  for (const u of candidates) {
    const from: Pos = { x: u.pos.x, y: u.pos.y };
    const dx = Math.sign(center.x - from.x);
    const dy = Math.sign(center.y - from.y);
    const dest: Pos = { x: from.x + dx, y: from.y + dy };

    if (dest.x < 0 || dest.x >= BATTLE_COLS || dest.y < 0 || dest.y >= BATTLE_ROWS) continue;
    if (baseTiles.has(`${dest.x},${dest.y}`)) continue;
    const blocker = snapByCell.get(`${dest.x},${dest.y}`);
    if (blocker && blocker.unit !== u) {
      const blockerIsCandidate = candSet.has(blocker.unit);
      if (!blockerIsCandidate && blocker.unit.team !== u.team) continue;
    }

    u.pos = dest;
    // Path preservation (Session 10 follow-up): keep planned route,
    // just fast-forward `pathIdx` to the closest remaining waypoint
    // (Manhattan) so units blown PAST a waypoint don't backtrack to
    // it. Units blown SIDEWAYS / BACKWARD keep their current waypoint
    // and walk back to it via movement.ts priority 2 (_stepToward).
    let bestIdx = u.pathIdx;
    let bestDist = bestIdx < u.path.length ? md(dest, u.path[bestIdx]) : Infinity;
    for (let i = u.pathIdx + 1; i < u.path.length; i++) {
      const d = md(dest, u.path[i]);
      // Strict-less so we keep the EARLIEST waypoint on tie, matching
      // the original route order.
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    // If we landed exactly ON the closest waypoint, advance past it so
    // movement.ts doesn't try to "walk to ourselves" (infinite no-op).
    while (
      bestIdx < u.path.length &&
      u.path[bestIdx].x === dest.x &&
      u.path[bestIdx].y === dest.y
    ) {
      bestIdx++;
    }
    u.pathIdx = bestIdx;
    moves.push({ unitId: u.id, from, to: dest, subPhase: 0 });
  }
  return moves;
}
